/**
 * Secret provider abstraction.
 *
 * Decouples credential resolution from process.env so secrets can be
 * provisioned over the transport bridge instead of living in the
 * container's environment.
 */

import { createLogger } from "@skaile/workspaces/core/logging";

const log = createLogger({ kind: "connector", subkind: "secrets" });

// ── Interfaces ──────────────────────────────────────────────────────────────

/**
 * Read-only secret resolution. Adapters receive this interface.
 * @docLink packages/connectors/api-reference#secret-provider
 */
export interface SecretProvider {
  /**
   * Resolve a credential reference.
   *
   * @param ref - Credential reference such as "env:GIT_TOKEN",
   *   "oauth:google:user@example.com", or an inline literal.
   * @returns The resolved secret value or undefined if not found.
   */
  resolve(ref: string): string | undefined;
  /**
   * Check whether a reference can be resolved.
   *
   * @param ref - Reference to check.
   * @returns True if resolve(ref) would return a non-undefined value.
   */
  has(ref: string): boolean;
  /**
   * Return all ref strings this provider can currently resolve.
   *
   * @returns All resolvable refs (used by forge UI and ConnectorFieldMissingError).
   */
  capabilities(): string[];
}

/**
 * Extends `SecretProvider` with the ability to load secrets at runtime via `provision()`.
 * @docLink packages/connectors/api-reference#provisionable-secret-provider
 */
export interface ProvisionableSecretProvider extends SecretProvider {
  /** Store a batch of key-value secrets. Keys are bare names (no prefix). */
  provision(secrets: Record<string, string>): void;
}

// ── Implementations ─────────────────────────────────────────────────────────

/**
 * In-memory secret store. Secrets are provisioned over the transport bridge
 * and stored in a private Map — never touching the filesystem or process.env.
 *
 * Supports ref prefixes:
 *   `"env:KEY"`   — looks up KEY in the store
 *   `"vault:KEY"` — looks up KEY in the store (future vault compat)
 *   (no prefix)   — returned as an inline value (backwards compat for non-chain usages)
 * @docLink packages/connectors/api-reference#in-memory-secret-provider
 */
export class InMemorySecretProvider implements ProvisionableSecretProvider {
  private readonly store = new Map<string, string>();

  /**
   * Store a batch of key-value secrets.
   *
   * @param secrets - Key-value map of secret names to values. Keys are bare names (no prefix).
   */
  provision(secrets: Record<string, string>): void {
    for (const [key, value] of Object.entries(secrets)) {
      this.store.set(key, value);
    }
  }

  resolve(ref: string): string | undefined {
    if (!ref) return undefined;
    if (ref.startsWith("env:")) return this.store.get(ref.slice(4));
    if (ref.startsWith("vault:")) return this.store.get(ref.slice(6));
    // Bare key: check store first, then fall back to inline literal
    const stored = this.store.get(ref);
    if (stored !== undefined) return stored;
    return ref;
  }

  has(ref: string): boolean {
    return this.resolve(ref) !== undefined;
  }

  capabilities(): string[] {
    return [...this.store.keys()].map((k) => `env:${k}`);
  }
}

/**
 * Delegates to `process.env`. Used in CLI/standalone mode.
 * Does NOT pass through inline literals — returns `undefined` for any ref
 * that does not start with `"env:"`. Use `InMemorySecretProvider` for inline
 * literals in non-chain contexts.
 * @docLink packages/connectors/api-reference#env-secret-provider
 */
export class EnvSecretProvider implements SecretProvider {
  resolve(ref: string): string | undefined {
    if (!ref) return undefined;
    if (ref.startsWith("env:")) return process.env[ref.slice(4)];
    return undefined; // no inline passthrough
  }

  has(ref: string): boolean {
    return this.resolve(ref) !== undefined;
  }

  capabilities(): string[] {
    return Object.keys(process.env).map((k) => `env:${k}`);
  }
}

/**
 * Pre-minted credential entry. Mirrors the discriminated `CredentialMint`
 * shape from `@skaile/workspaces/types` but redeclared here to keep this package
 * free of cross-package type dependencies.
 *
 * The failure branch carries the platform-side `code` + `message` so the
 * `ConnectorManager`'s wrapped mediator can surface the real reason instead of
 * a generic "no pre-mint" placeholder when a mint comes back as `ok: false`.
 */
export type PreMintedCredential =
  | { ok: true; token: string; expiresAt: string | null; mintedAt?: string }
  | {
      ok: false;
      code?: "not-configured" | "revoked" | "provider-error" | "backend-error";
      message?: string;
    };

/**
 * Pre-minted credential store. Holds short-lived tokens delivered upfront in
 * the v3 `session_init` envelope so the runner never has to call back to the
 * platform during mount setup.
 *
 * Resolves refs of the form `mount:<id>`, `connector:<id>`, and `mcp:<id>`.
 * Returns `undefined` for any other ref so the chain falls through to the
 * next provider (`EnvSecretProvider` for `pat:env:NAME`, etc.).
 *
 * Tokens can be added/removed at runtime via {@link set} / {@link unset} so
 * `runner.add_mount` / `runner.add_connector` / `runner.add_mcp` capabilities
 * stay strictly in-process — no round trip to the platform to mint while
 * the resource is hot-attached.
 *
 * For callers that need the full mint metadata (expiry timestamp, mintedAt
 * audit fields), use {@link mintFor} instead of the bare `resolve`. The
 * `ConnectorManager` consults this for the `auth: backend` initial-mint path so
 * the driver retains the original expiry semantics.
 *
 * Spec: `_devlog/specs/2026-05-10-deterministic-session-bootstrap.md`.
 *
 * @docLink packages/connectors/api-reference#pre-minted-secret-provider
 */
export class PreMintedSecretProvider implements SecretProvider {
  private readonly mints = new Map<string, PreMintedCredential>();

  /**
   * Seed pre-minted credentials from the v3 `session_init.credentials` map.
   *
   * Failed mints (`ok: false`) are stored too so callers can distinguish
   * "not pre-minted" from "pre-minted but failed"; {@link resolve} treats
   * both as `undefined`.
   *
   * @param credentials Optional initial `{ mounts, connectors, mcp }` map.
   */
  constructor(credentials?: {
    mounts?: Record<string, PreMintedCredential>;
    connectors?: Record<string, PreMintedCredential>;
    mcp?: Record<string, PreMintedCredential>;
  }) {
    if (!credentials) return;
    for (const [id, mint] of Object.entries(credentials.mounts ?? {})) {
      this.mints.set(`mount:${id}`, mint);
    }
    for (const [id, mint] of Object.entries(credentials.connectors ?? {})) {
      this.mints.set(`connector:${id}`, mint);
    }
    for (const [id, mint] of Object.entries(credentials.mcp ?? {})) {
      this.mints.set(`mcp:${id}`, mint);
    }
  }

  /**
   * Insert (or replace) a pre-minted credential. Used by
   * `runner.add_mount` / `runner.add_connector` / `runner.add_mcp` capability
   * handlers when the platform delivers new credentials mid-session.
   */
  set(kind: "mount" | "connector" | "mcp", id: string, mint: PreMintedCredential): void {
    this.mints.set(`${kind}:${id}`, mint);
  }

  /**
   * Remove a stored pre-minted credential. Used by the
   * `runner.remove_resource` capability handler.
   */
  unset(kind: "mount" | "connector" | "mcp", id: string): boolean {
    return this.mints.delete(`${kind}:${id}`);
  }

  /**
   * Look up the full {@link PreMintedCredential} for a `mount:` /
   * `connector:` / `mcp:` keyed entry. Callers that only need the token use
   * {@link resolve} via the chain; callers that need expiry / audit
   * metadata (e.g. ConnectorManager) use this entry point.
   */
  mintFor(kind: "mount" | "connector" | "mcp", id: string): PreMintedCredential | undefined {
    return this.mints.get(`${kind}:${id}`);
  }

  resolve(ref: string): string | undefined {
    if (!ref) return undefined;
    if (!(ref.startsWith("mount:") || ref.startsWith("connector:") || ref.startsWith("mcp:")))
      return undefined;
    const mint = this.mints.get(ref);
    return mint?.ok ? mint.token : undefined;
  }

  has(ref: string): boolean {
    return this.resolve(ref) !== undefined;
  }

  capabilities(): string[] {
    return [...this.mints.entries()].filter(([, m]) => m.ok).map(([k]) => k);
  }
}

/**
 * Forge session secret provider. Secrets are provisioned from the Forge settings
 * panel over the transport bridge. Only handles `"forge:"` prefixed refs — returns
 * `undefined` for all others.
 * @docLink packages/connectors/api-reference#forge-secret-provider
 */
export class ForgeSecretProvider implements ProvisionableSecretProvider {
  private readonly store = new Map<string, string>();

  provision(secrets: Record<string, string>): void {
    for (const [key, value] of Object.entries(secrets)) {
      this.store.set(key, value);
    }
  }

  resolve(ref: string): string | undefined {
    if (!ref) return undefined;
    if (ref.startsWith("forge:")) return this.store.get(ref.slice(6));
    return undefined;
  }

  has(ref: string): boolean {
    return this.resolve(ref) !== undefined;
  }

  capabilities(): string[] {
    return [...this.store.keys()].map((k) => `forge:${k}`);
  }
}

/**
 * Thrown when an OAuth-secured connector field requires user authorization before
 * it can be resolved. Contains the redirect URL to open in the user's browser.
 * @docLink packages/connectors/api-reference#o-auth-required-error
 */
export class OAuthRequiredError extends Error {
  readonly oauthRef: string;
  readonly authUrl: string;
  readonly state: string;

  /**
   * @param oauthRef - The 'oauth:<service>:<account>' ref that triggered the error
   * @param authUrl - Browser URL to open for OAuth authorization
   * @param state - OAuth state token for CSRF protection and callback correlation
   */
  constructor(oauthRef: string, authUrl: string, state: string) {
    super(`OAuth authorization required for ${oauthRef}`);
    this.name = "OAuthRequiredError";
    this.oauthRef = oauthRef;
    this.authUrl = authUrl;
    this.state = state;
  }
}

/**
 * Shape of an OAuth token bundle stored by `OAuthSecretProvider`.
 * @docLink packages/connectors/api-reference#o-auth-token-bundle
 */
export interface OAuthTokenBundle {
  accessToken: string;
  refreshToken?: string;
  /** Unix timestamp in ms */
  expiry?: number;
}

/**
 * Return value of `OAuthSecretProvider.initiateFlow()`. Contains the URL and CSRF state token.
 * @docLink packages/connectors/api-reference#o-auth-flow-request
 */
export interface OAuthFlowRequest {
  authUrl: string;
  state: string;
}

/**
 * OAuth token provider. Handles `"oauth:<service>:<account>"` refs.
 * Callers must call `chain.prepareRef()` before `chain.resolve()` for `oauth:` refs.
 * @docLink packages/connectors/api-reference#o-auth-secret-provider
 */
export interface OAuthSecretProvider extends SecretProvider {
  /**
   * Ensure the token for this ref is fresh.
   * Refreshes if expired.
   * Throws OAuthRequiredError if no token exists and user authorization is needed.
   */
  ensureFresh(ref: string): Promise<void>;

  /** Initiate the OAuth authorization flow. Returns URL to open in the user's browser. */
  initiateFlow(oauthService: string, account: string): Promise<OAuthFlowRequest>;

  /** Exchange authorization code for tokens and store the bundle. */
  completeFlow(state: string, code: string): Promise<void>;
}

/**
 * Abstract base for vault-backed secret providers (1Password, KeePass). Subclasses
 * implement `prefix` and `loadKeys()`. Locked until `unlock(credential)` is called.
 * @docLink packages/connectors/api-reference#locked-secret-provider
 */
export abstract class LockedSecretProvider implements SecretProvider {
  locked = true;
  protected readonly store = new Map<string, string>();
  abstract readonly prefix: string;

  async unlock(credential: string): Promise<void> {
    await this.loadKeys(credential);
    this.locked = false;
  }

  protected abstract loadKeys(credential: string): Promise<void>;

  resolve(ref: string): string | undefined {
    if (this.locked) return undefined;
    if (!ref.startsWith(this.prefix)) return undefined;
    return this.store.get(ref.slice(this.prefix.length));
  }

  has(ref: string): boolean {
    return this.resolve(ref) !== undefined;
  }

  capabilities(): string[] {
    if (this.locked) return [];
    return [...this.store.keys()].map((k) => `${this.prefix}${k}`);
  }
}

// ── Known provider prefixes ──────────────────────────────────────────────────

const KNOWN_PREFIXES = [
  "env:",
  "vault:",
  "forge:",
  "op:",
  "kp:",
  "oauth:",
  "mount:",
  "connector:",
  "mcp:",
] as const;

function getPrefix(ref: string): string | undefined {
  return KNOWN_PREFIXES.find((p) => ref.startsWith(p));
}

/**
 * True when `value` starts with a known secret-ref prefix (`env:`, `oauth:`,
 * `mcp:`, ...). Lets callers distinguish an intended-but-unresolved ref from a
 * plain literal — e.g. the runner refuses to forward an unresolved ref into
 * MCP transport headers/env, where the literal would otherwise leak onto the
 * wire and produce a misleading upstream error.
 */
export function isKnownSecretRef(value: string): boolean {
  return getPrefix(value) !== undefined;
}

/**
 * Chains multiple `SecretProvider` instances into a single resolver.
 * - Prefixed refs (`env:`, `forge:`, `op:`, `kp:`, `oauth:`) are routed to the first provider that can resolve them.
 * - Bare refs waterfall through all providers in order.
 * - Plain literals in `skaile.yaml` options are NOT passed to the chain; `ConnectorManager` handles them before calling `resolve()`.
 * @docLink packages/connectors/api-reference#secret-provider-chain
 */
export class SecretProviderChain implements SecretProvider {
  /**
   * @param providers - Ordered list of providers. For prefixed refs, the first
   *   provider that can resolve wins. For bare refs, waterfall through all in order.
   */
  constructor(private readonly providers: SecretProvider[]) {}

  resolve(ref: string): string | undefined {
    if (!ref) return undefined;
    const prefix = getPrefix(ref);
    log.debug("resolve secret", { ref, prefix: prefix ?? "(bare)" });
    if (prefix) {
      for (const p of this.providers) {
        const val = p.resolve(ref);
        if (val !== undefined) return val;
      }
      log.warn("secret not resolvable", { ref });
      return undefined;
    }
    // Waterfall for bare refs
    for (const p of this.providers) {
      const val = p.resolve(ref);
      if (val !== undefined) return val;
    }
    log.warn("secret not resolvable", { ref });
    return undefined;
  }

  has(ref: string): boolean {
    return this.resolve(ref) !== undefined;
  }

  capabilities(): string[] {
    const all = this.providers.flatMap((p) => p.capabilities());
    return [...new Set(all)];
  }

  /** Returns the first provider that can resolve this ref. */
  providerFor(ref: string): SecretProvider | undefined {
    for (const p of this.providers) {
      if (p.resolve(ref) !== undefined) return p;
    }
    return undefined;
  }

  /**
   * Calls ensureFresh(ref) on the owning provider if it implements OAuthSecretProvider.
   * No-op for providers that don't have ensureFresh().
   *
   * @param ref - Must be an "oauth:" prefixed ref for this to have any effect.
   *   Calls ensureFresh() on the owning OAuthSecretProvider.
   */
  async prepareRef(ref: string): Promise<void> {
    const owner = this.providerFor(ref);
    if (
      owner &&
      "ensureFresh" in owner &&
      typeof (owner as OAuthSecretProvider).ensureFresh === "function"
    ) {
      try {
        await (owner as OAuthSecretProvider).ensureFresh(ref);
      } catch (err) {
        log.error("secret provider ensureFresh failed", err, { ref });
        throw err;
      }
    }
  }
}
